import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Check, BookOpen, FlaskConical, Award, Download, ShieldCheck, HelpCircle, Zap, Users, Target, Lock, Plus, Calendar, FileText, Video, Layers, Infinity, Flame, ArrowRight, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { CountdownBar } from '../components/CountdownBar';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const fadeInScale = {
  hidden: {
    opacity: 0,
    scale: 0.98
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export const LandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const previewImages = ['https://cdn.magicpatterns.com/uploads/asjKvEo9AGEfQK9PtgFNek/Captura_de_pantalla_2026-02-04_a_la(s)_10.19.54_a._m..png', 'https://cdn.magicpatterns.com/uploads/tKfEn9aLKrGwZLLhTCgYsX/Captura_de_pantalla_2026-02-04_a_la(s)_10.20.03_a._m..png', 'https://cdn.magicpatterns.com/uploads/6tAMowRq8ye3KhARNCN3qh/Captura_de_pantalla_2026-02-04_a_la(s)_10.20.23_a._m..png', 'https://cdn.magicpatterns.com/uploads/aX7C7bWHr7gYm3AjnuVDTb/Captura_de_pantalla_2026-02-04_a_la(s)_10.20.46_a._m..png', 'https://cdn.magicpatterns.com/uploads/rFQQhv3efZxSHoqzqbdyWT/Captura_de_pantalla_2026-02-04_a_la(s)_10.21.06_a._m..png', 'https://cdn.magicpatterns.com/uploads/4GtNKmH7QeQ1N7fWJpjHch/Captura_de_pantalla_2026-02-04_a_la(s)_10.21.28_a._m..png', 'https://cdn.magicpatterns.com/uploads/aFtpxXcWXq2oDTU2EbMjiP/Captura_de_pantalla_2026-02-04_a_la(s)_10.21.42_a._m..png', 'https://cdn.magicpatterns.com/uploads/aeNvqTEHdXhgWNUJWq8k2W/Captura_de_pantalla_2026-02-04_a_la(s)_10.22.03_a._m..png'];
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % previewImages.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + previewImages.length) % previewImages.length);
  };
  const scrollToPricing = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  // Clickfunnels style button classes
  const ctaButtonPrimary = 'w-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 text-white font-black text-lg md:text-xl uppercase py-5 px-6 rounded-lg shadow-[0_4px_0_0_#047857] hover:shadow-[0_2px_0_0_#047857] hover:translate-y-[2px] transition-all duration-150 border-0';
  const ctaButtonSecondary = 'w-full bg-gradient-to-b from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white font-black text-lg md:text-xl uppercase py-5 px-6 rounded-lg shadow-[0_4px_0_0_#1f2937] hover:shadow-[0_2px_0_0_#1f2937] hover:translate-y-[2px] transition-all duration-150 border-0';
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-36">
      <CountdownBar data-id="element-37" />
      <PurchaseNotification data-id="element-38" />

      {/* Hero Section - Compact */}
      <section className="py-6 px-4" data-id="element-39">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-40">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold mb-3" data-id="element-41">
            <FlaskConical className="w-4 h-4" data-id="element-42" />
            <span data-id="element-43">PARA DOCENTES DE FÍSICA</span>
          </motion.div>

          {/* Main Title - NEW */}
          <motion.h1 initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl md:text-3xl lg:text-4xl font-black mb-4 leading-tight tracking-tight text-gray-900" data-id="element-44">
            Organizá todo tu año de Física y{' '}
            <span className="text-emerald-600" data-id="element-45">
              ahorrá hasta 12 meses de planificación
            </span>{' '}
            con el Mega Pack Física Lista
          </motion.h1>

          {/* Product Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex justify-center mb-4" data-id="element-46">
            <img src="https://cdn.magicpatterns.com/uploads/vEi2128WGozt2JakGts25X/PROYECTOS_Anuales_(1080_x_900_px)_(1).png" alt="Mega Pack Física en Acción" className="w-full max-w-[240px] md:max-w-[320px] cursor-pointer" onClick={scrollToPricing} data-id="element-47" />
          </motion.div>

          {/* Product Name */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.4
        }} className="flex items-center justify-center gap-2 text-emerald-700 font-black text-lg mb-2" data-id="element-48">
            <FlaskConical className="w-5 h-5" data-id="element-49" />
            <span data-id="element-50">MEGAPACK FÍSICA LISTA™</span>
          </motion.div>

          {/* Description */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.35,
          duration: 0.4
        }} className="text-gray-600 text-sm md:text-base mb-4 max-w-lg mx-auto" data-id="element-51">
            Sistema completo de clases, actividades y proyectos de física listos
            para usar.
            <br data-id="element-52" />
            <span className="font-semibold text-gray-800" data-id="element-53">
              Todo tu año resuelto (todos los niveles)
            </span>
          </motion.p>

          {/* CTA Button - ClickFunnels Style */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4,
          duration: 0.4
        }} className="max-w-md mx-auto mb-3" data-id="element-54">
            <button onClick={scrollToPricing} className="w-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 text-white font-black text-base md:text-lg uppercase py-4 px-5 rounded-lg shadow-[0_4px_0_0_#047857] hover:shadow-[0_2px_0_0_#047857] hover:translate-y-[2px] transition-all duration-150 border-0" data-id="element-55">
              <span className="flex items-center justify-center gap-2" data-id="element-56">
                <span data-id="element-57">👉 SÍ, QUIERO ACCESO INMEDIATO</span>
                <ArrowRight className="w-4 h-4" data-id="element-58" />
              </span>
            </button>
          </motion.div>

          {/* Trust Badges - Compact */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5,
          duration: 0.4
        }} className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500" data-id="element-59">
            <span className="flex items-center gap-1" data-id="element-60">
              <Lock className="w-3 h-3" data-id="element-61" /> Pago seguro
            </span>
            <span className="flex items-center gap-1" data-id="element-62">
              <Download className="w-3 h-3" data-id="element-63" /> Descarga inmediata
            </span>
            <span className="flex items-center gap-1" data-id="element-64">
              <ShieldCheck className="w-3 h-3" data-id="element-65" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-66">
              <Infinity className="w-3 h-3" data-id="element-67" /> Acceso de por vida
            </span>
          </motion.div>
        </div>
      </section>

      {/* Problem Section - Compact */}
      <section className="py-8 px-4 bg-gray-50" data-id="element-68">
        <div className="container mx-auto max-w-3xl" data-id="element-69">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-6" data-id="element-70">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" data-id="element-71">
              Si enseñás Física, esto te va a{' '}
              <span className="text-emerald-600" data-id="element-72">sonar familiar…</span>
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-73">
            <p className="text-base text-gray-700 leading-relaxed mb-4" data-id="element-74">
              Llegás del trabajo <strong data-id="element-75">agotado</strong> y tenés que
              planificar. Horas buscando proyectos, diseñando experiencias,
              armando evaluaciones...
            </p>
            <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-600" data-id="element-76">
              <p className="text-base text-gray-800" data-id="element-77">
                <strong data-id="element-78">No es falta de capacidad.</strong> Es que te falta un{' '}
                <span className="text-emerald-700 font-bold" data-id="element-79">
                  sistema integral
                </span>{' '}
                que te sostenga todo el año.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section - Compact */}
      <section className="py-8 px-4" data-id="element-80">
        <div className="container mx-auto max-w-4xl" data-id="element-81">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-6" data-id="element-82">
            <span className="inline-block bg-emerald-700 text-white font-bold px-4 py-1.5 rounded-md text-sm uppercase mb-4" data-id="element-83">
              La Solución
            </span>
            <h3 className="text-2xl md:text-4xl font-black text-gray-900" data-id="element-84">
              MEGA PACK{' '}
              <span className="text-emerald-600" data-id="element-85">FÍSICA EN ACCIÓN™</span>
            </h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto mt-3" data-id="element-86">
              Sistema completo de proyectos, experiencias y evaluaciones{' '}
              <strong data-id="element-87">ya probadas en aula</strong>.
            </p>
          </motion.div>

          {/* Features Grid - Compact */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-4 gap-2 mb-6" data-id="element-88">
            {[{
            icon: Check,
            text: 'Listo'
          }, {
            icon: FlaskConical,
            text: 'Probado'
          }, {
            icon: BookOpen,
            text: 'Organizado'
          }, {
            icon: Zap,
            text: 'Usable'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="bg-emerald-50 p-3 rounded-lg text-center" data-id="element-89">
                <item.icon className="w-5 h-5 text-emerald-600 mx-auto mb-1" data-id="element-90" />
                <span className="font-bold text-gray-900 text-xs" data-id="element-91">
                  {item.text}
                </span>
              </motion.div>)}
          </motion.div>

          {/* What's included - Compact */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-92">
            <h4 className="text-xl font-black text-gray-900 mb-4 text-center" data-id="element-93">
              ¿Qué Incluye?
            </h4>
            <div className="grid md:grid-cols-2 gap-3" data-id="element-94">
              {[{
              icon: Layers,
              text: '6 tomos completos (1.º a 6.º año)'
            }, {
              icon: FileText,
              text: '60+ fichas editables'
            }, {
              icon: Calendar,
              text: 'Proyectos anuales listos'
            }, {
              icon: Check,
              text: 'Planillas y rúbricas'
            }, {
              icon: Video,
              text: 'Videos demostrativos'
            }, {
              icon: Award,
              text: 'Material para ferias'
            }].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-95">
                  <div className="bg-emerald-100 p-1.5 rounded" data-id="element-96">
                    <item.icon className="w-4 h-4 text-emerald-700" data-id="element-97" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-98">{item.text}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Material Preview Carousel */}
      <section className="py-8 px-4 bg-gray-50" data-id="element-99">
        <div className="container mx-auto max-w-4xl" data-id="element-100">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-6" data-id="element-101">
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 font-bold px-4 py-1.5 rounded-md text-sm mb-3" data-id="element-102">
              <Eye className="w-4 h-4" data-id="element-103" />
              MIRÁ POR DENTRO
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" data-id="element-104">
              Páginas reales del{' '}
              <span className="text-emerald-600" data-id="element-105">material</span>
            </h2>
            <p className="text-gray-600 text-sm mt-2" data-id="element-106">
              Proyectos paso a paso, clases listas y actividades prácticas
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="relative" data-id="element-107">
            {/* Carousel Container */}
            <div className="relative overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200" data-id="element-108">
              <div className="relative aspect-[3/4] md:aspect-[4/3] max-h-[500px]" data-id="element-109">
                {previewImages.map((img, index) => <motion.div key={index} initial={false} animate={{
                opacity: currentSlide === index ? 1 : 0,
                scale: currentSlide === index ? 1 : 0.95
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className={`absolute inset-0 ${currentSlide === index ? 'z-10' : 'z-0'}`} data-id="element-110">
                    <img src={img} alt={`Vista previa del material ${index + 1}`} className="w-full h-full object-contain" data-id="element-111" />
                  </motion.div>)}
              </div>

              {/* Navigation Buttons */}
              <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all hover:scale-110" aria-label="Anterior" data-id="element-112">
                <ChevronLeft className="w-5 h-5" data-id="element-113" />
              </button>
              <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all hover:scale-110" aria-label="Siguiente" data-id="element-114">
                <ChevronRight className="w-5 h-5" data-id="element-115" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4" data-id="element-116">
              {previewImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-all ${currentSlide === index ? 'bg-emerald-600 w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-117" />)}
            </div>

            {/* Slide Counter */}
            <p className="text-center text-gray-500 text-xs mt-2" data-id="element-118">
              {currentSlide + 1} de {previewImages.length}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Results Section - Compact */}
      <section className="py-8 px-4 bg-gray-50" data-id="element-119">
        <div className="container mx-auto max-w-4xl" data-id="element-120">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-3xl font-black text-center text-gray-900 mb-6" data-id="element-121">
            Resultados que <span className="text-emerald-600" data-id="element-122">transforman</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-4" data-id="element-123">
            {/* Students */}
            <motion.div variants={staggerItem} className="bg-white p-5 rounded-xl border border-gray-100" data-id="element-124">
              <div className="flex items-center gap-2 mb-4" data-id="element-125">
                <div className="bg-emerald-600 p-2 rounded-lg" data-id="element-126">
                  <Users className="w-4 h-4 text-white" data-id="element-127" />
                </div>
                <h3 className="text-lg font-black text-gray-900" data-id="element-128">
                  Para tus Estudiantes
                </h3>
              </div>
              <ul className="space-y-2" data-id="element-129">
                {['Comprenden desde la práctica', 'Conectan teoría y realidad', 'Desarrollan razonamiento científico', 'Participan más en clase'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-700 text-sm" data-id="element-130">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-131" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Teachers */}
            <motion.div variants={staggerItem} className="bg-white p-5 rounded-xl border border-gray-100" data-id="element-132">
              <div className="flex items-center gap-2 mb-4" data-id="element-133">
                <div className="bg-gray-900 p-2 rounded-lg" data-id="element-134">
                  <Target className="w-4 h-4 text-white" data-id="element-135" />
                </div>
                <h3 className="text-lg font-black text-gray-900" data-id="element-136">
                  Para Vos como Docente
                </h3>
              </div>
              <ul className="space-y-2" data-id="element-137">
                {['Ahorrás horas de planificación', 'Enseñás con claridad', 'Todo organizado', 'Llegás sin estrés al cierre'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-700 text-sm" data-id="element-138">
                    <Check className="w-4 h-4 text-gray-900 flex-shrink-0" data-id="element-139" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-6 max-w-md mx-auto" data-id="element-140">
            <button onClick={scrollToPricing} className={ctaButtonPrimary} data-id="element-141">
              <span className="flex items-center justify-center gap-2" data-id="element-142">
                👉 QUIERO LOGRAR ESTO
                <ArrowRight className="w-5 h-5" data-id="element-143" />
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Bonuses Section - Compact */}
      <section className="py-8 px-4" data-id="element-144">
        <div className="container mx-auto max-w-4xl" data-id="element-145">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-6" data-id="element-146">
            <span className="inline-block bg-gray-900 text-white font-bold px-4 py-1.5 rounded-md text-sm mb-3" data-id="element-147">
              🎁 BONOS GRATIS HOY
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" data-id="element-148">
              Te llevas{' '}
              <span className="text-emerald-600" data-id="element-149">4 bonos exclusivos</span>
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-150">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/4KbjkFbxfSoGJ2E6KgrWGW/2.png',
            title: 'BONO 1',
            name: 'Manual Física Tradicional',
            description: 'Guía completa con teoría y ejercicios clásicos para reforzar conceptos fundamentales.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/77rEtYXjC7DumCQvGEFXP8/3.png',
            title: 'BONO 2',
            name: 'Láminas Editables',
            description: 'Recursos visuales listos para imprimir y personalizar según tu clase.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/ojGq5GQBhnQqxgj7daWY9Z/4.png',
            title: 'BONO 3',
            name: 'Cuadernillos Editables',
            description: 'Material de trabajo para estudiantes que podés adaptar a tu planificación.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/hPKFs1GnUyX15im4JyuqmX/ChatGPT_Image_4_feb_2026,_11_48_18_a.m..png',
            title: 'BONO 4',
            name: 'Proyectos + 5 Cuadernillos',
            description: 'Ideas de proyectos prácticos con cuadernillos de apoyo incluidos.'
          }].map((bonus, i) => <motion.div key={i} variants={staggerItem} className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm" data-id="element-151">
                <div className="flex items-center justify-between mb-2" data-id="element-152">
                  <span className="bg-emerald-600 text-white font-bold text-[10px] px-2 py-0.5 rounded" data-id="element-153">
                    {bonus.title}
                  </span>
                  <span className="bg-gray-900 text-white font-bold text-[10px] px-2 py-0.5 rounded" data-id="element-154">
                    GRATIS
                  </span>
                </div>
                <img src={bonus.img} alt={bonus.name} className="w-full rounded mb-2 cursor-pointer" onClick={scrollToPricing} data-id="element-155" />
                <h3 className="font-bold text-gray-900 text-xs mb-1" data-id="element-156">
                  {bonus.name}
                </h3>
                <p className="text-gray-500 text-[10px] leading-tight" data-id="element-157">
                  {bonus.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section - ClickFunnels Style */}
      <section id="compra" className="py-6 px-3 bg-emerald-50 scroll-mt-4" data-id="element-158">
        <div className="container mx-auto max-w-4xl" data-id="element-159">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-4" data-id="element-160">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 font-bold px-3 py-1 rounded-md text-xs mb-1" data-id="element-161">
              <Flame className="w-3 h-3" data-id="element-162" />
              🔥 OFERTA ESPECIAL POR TIEMPO LIMITADO
            </div>
          </motion.div>

          {/* Two cards stacked vertically, centered */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="flex flex-col gap-4 max-w-md mx-auto" data-id="element-163">
            {/* Card 1 - Megapack */}
            <motion.div variants={staggerItem} className="bg-white rounded-xl shadow-xl border-2 border-emerald-500 overflow-hidden" data-id="element-164">
              <div className="bg-emerald-600 text-white py-2 px-3 text-center" data-id="element-165">
                <div className="flex items-center justify-center gap-2 text-sm font-semibold" data-id="element-166">
                  <Flame className="w-4 h-4" data-id="element-167" />
                  <span data-id="element-168">MEGAPACK FÍSICA LISTA</span>
                </div>
              </div>
              <div className="p-4" data-id="element-169">
                <p className="text-center text-gray-600 text-sm mb-3" data-id="element-170">
                  6 Tomos Base + 4 Bonos PRO
                </p>
                <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-171">
                  {['Pago único', 'Acceso de por vida', 'Descarga inmediata', 'Garantía 7 días'].map((item, i) => <div key={i} className="bg-emerald-50 rounded-lg p-2 text-center" data-id="element-172">
                      <Check className="w-3 h-3 text-emerald-600 mx-auto mb-0.5" data-id="element-173" />
                      <span className="text-[10px] font-medium text-gray-700" data-id="element-174">
                        {item}
                      </span>
                    </div>)}
                </div>
                <div className="text-center mb-3" data-id="element-175">
                  <p className="text-red-500 font-semibold line-through text-xs" data-id="element-176">
                    Precio regular $49.99 USD
                  </p>
                  <div className="flex items-baseline justify-center gap-1 mt-0.5" data-id="element-177">
                    <span className="text-4xl font-black text-gray-900" data-id="element-178">
                      $9.97
                    </span>
                    <span className="text-base text-gray-600" data-id="element-179">USD</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5" data-id="element-180">
                    Pago único · Pagarás en tu moneda local
                  </p>
                </div>
                <a href="https://docentes.edulabs.online/cart/47462806847737:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 text-white font-black text-sm uppercase py-3 px-4 rounded-lg shadow-[0_3px_0_0_#047857] hover:shadow-[0_1px_0_0_#047857] hover:translate-y-[2px] transition-all text-center" data-id="element-181">
                  <span className="flex items-center justify-center gap-2" data-id="element-182">
                    👉 SÍ, QUIERO EL MEGAPACK
                    <ArrowRight className="w-4 h-4" data-id="element-183" />
                  </span>
                </a>
              </div>
            </motion.div>

            {/* Card 2 - Super Megapack */}
            <motion.div variants={staggerItem} className="bg-white rounded-xl shadow-xl border-2 border-gray-900 overflow-hidden" data-id="element-184">
              <div className="bg-gray-900 text-white py-2 px-3 text-center" data-id="element-185">
                <div className="flex items-center justify-center gap-2 text-sm font-semibold" data-id="element-186">
                  <Plus className="w-4 h-4" data-id="element-187" />
                  <span data-id="element-188">⭐ MÁS POPULAR — FÍSICA + QUÍMICA</span>
                </div>
              </div>
              <div className="p-4" data-id="element-189">
                <p className="text-center text-gray-600 text-sm mb-3" data-id="element-190">
                  12 Cuadernillos completos + 7 Bonos
                </p>
                <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-191">
                  {['Física completa', 'Química completa', '6 Bonos', 'Garantía 7 días'].map((item, i) => <div key={i} className="bg-gray-50 rounded-lg p-2 text-center" data-id="element-192">
                      <Check className="w-3 h-3 text-emerald-600 mx-auto mb-0.5" data-id="element-193" />
                      <span className="text-[10px] font-medium text-gray-700" data-id="element-194">
                        {item}
                      </span>
                    </div>)}
                </div>
                <div className="text-center mb-3" data-id="element-195">
                  <p className="text-red-500 font-semibold line-through text-xs" data-id="element-196">
                    Valor separado $25.98 USD
                  </p>
                  <div className="flex items-baseline justify-center gap-1 mt-0.5" data-id="element-197">
                    <span className="text-4xl font-black text-gray-900" data-id="element-198">
                      $14.99
                    </span>
                    <span className="text-base text-gray-600" data-id="element-199">USD</span>
                  </div>
                  <p className="text-emerald-600 font-bold text-xs mt-0.5" data-id="element-200">
                    ¡Ahorrás $10.99 USD más!
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-201">
                    Pago único · Pagarás en tu moneda local
                  </p>
                </div>
                <a href="https://docentes.edulabs.online/cart/47462806880505:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-b from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white font-black text-sm uppercase py-3 px-4 rounded-lg shadow-[0_3px_0_0_#1f2937] hover:shadow-[0_1px_0_0_#1f2937] hover:translate-y-[2px] transition-all text-center" data-id="element-202">
                  <span className="flex items-center justify-center gap-2" data-id="element-203">
                    👉 SÍ, QUIERO EL SUPER MEGAPACK
                    <ArrowRight className="w-4 h-4" data-id="element-204" />
                  </span>
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Payment badges */}
          <div className="mt-3 text-center" data-id="element-205">
            <div className="flex items-center justify-center gap-3 opacity-40 grayscale" data-id="element-206">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-4" data-id="element-207" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-4" data-id="element-208" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1 flex items-center justify-center gap-1" data-id="element-209">
              <Lock className="w-3 h-3" data-id="element-210" /> SSL 256 bits · Procesamiento seguro
            </p>
          </div>
        </div>
      </section>

      {/* Guarantee - Compact */}
      <section className="py-6 px-4" data-id="element-211">
        <div className="container mx-auto max-w-2xl" data-id="element-212">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="flex items-center gap-4 bg-emerald-50 p-4 rounded-xl border border-emerald-200" data-id="element-213">
            <div className="bg-emerald-600 p-2.5 rounded-lg flex-shrink-0" data-id="element-214">
              <ShieldCheck className="w-6 h-6 text-white" data-id="element-215" />
            </div>
            <div data-id="element-216">
              <h3 className="text-base font-black text-gray-900 mb-0.5" data-id="element-217">
                Garantía de Satisfacción Total
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-218">
                Probalo <strong data-id="element-219">7 días</strong>. Si no cumple, te devolvemos el{' '}
                <strong className="text-emerald-600" data-id="element-220">100%</strong>. Sin
                preguntas.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials - Compact */}
      <section className="py-8 px-4 bg-gray-50" data-id="element-221">
        <div className="container mx-auto max-w-4xl" data-id="element-222">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl font-black text-center text-gray-900 mb-6" data-id="element-223">
            Testimonios de Docentes
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-4" data-id="element-224">
            {[{
            name: 'María G.',
            location: 'Bogotá, Colombia',
            quote: 'Ahora organizo todo el año sin estrés. Es increíble el tiempo que ahorro.'
          }, {
            name: 'Gustavo L.',
            location: 'Ciudad de México',
            quote: 'Los estudiantes se involucraron más. Las clases cambiaron por completo.'
          }, {
            name: 'Roberto P.',
            location: 'Buenos Aires',
            quote: 'Mis clases son más dinámicas y los alumnos participan activamente.'
          }].map((t, i) => <motion.div key={i} variants={staggerItem} data-id="element-225">
                <TestimonialCard name={t.name} location={t.location} quote={t.quote} data-id="element-226" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* FAQ - Compact */}
      <section className="py-8 px-4" data-id="element-227">
        <div className="container mx-auto max-w-2xl" data-id="element-228">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl font-black text-center text-gray-900 mb-6" data-id="element-229">
            Preguntas Frecuentes
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-3" data-id="element-230">
            {[{
            q: '¿En qué formato recibo el material?',
            a: '100% digital: PDF descargables listos para usar.'
          }, {
            q: '¿Puedo imprimir los cuadernillos?',
            a: 'Sí, sin restricciones.'
          }, {
            q: '¿Para qué niveles sirve?',
            a: 'De 1.º a 6.º año de Secundaria/Bachillerato.'
          }, {
            q: '¿Necesito laboratorio?',
            a: 'No. Se adaptan a materiales accesibles.'
          }, {
            q: '¿Cuánto tarda en llegar?',
            a: 'Acceso inmediato tras la compra.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-231">
                <h3 className="font-bold text-gray-900 text-sm flex items-start gap-2" data-id="element-232">
                  <HelpCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" data-id="element-233" />
                  {item.q}
                </h3>
                <p className="text-gray-600 text-sm ml-6 mt-1" data-id="element-234">{item.a}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Final CTA - Compact */}
      <section className="py-10 px-4 bg-emerald-600 text-center" data-id="element-235">
        <div className="container mx-auto max-w-2xl" data-id="element-236">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-237">
            <h2 className="text-2xl md:text-3xl font-black mb-3 text-white" data-id="element-238">
              ¿Listo para transformar tu año escolar?
            </h2>
            <p className="text-emerald-100 mb-6" data-id="element-239">
              Únete a cientos de docentes que ya organizaron su año sin estrés
            </p>
            <button onClick={scrollToPricing} className="bg-white hover:bg-gray-100 text-emerald-700 font-black text-lg uppercase py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all" data-id="element-240">
              <span className="flex items-center justify-center gap-2" data-id="element-241">
                👉 QUIERO EMPEZAR AHORA
                <ArrowRight className="w-5 h-5" data-id="element-242" />
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer - Compact */}
      <footer className="bg-gray-900 text-gray-400 py-6 px-4 text-center" data-id="element-243">
        <div className="container mx-auto max-w-4xl" data-id="element-244">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-245">
            <FlaskConical className="w-4 h-4 text-emerald-500" data-id="element-246" />
            <span className="text-white font-bold text-sm" data-id="element-247">
              MEGA PACK FÍSICA EN ACCIÓN™
            </span>
          </div>
          <p className="text-xs mb-3" data-id="element-248">
            © 2026 Mega Pack Física en Acción™. Todos los derechos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-xs" data-id="element-249">
            <a href="#" className="hover:text-white transition-colors" data-id="element-250">
              Términos
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-251">
              Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-252">
              Soporte
            </a>
          </div>
        </div>
      </footer>
    </div>;
};